import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  verifyResetCode,
  completePasswordReset,
  authErrorMessage,
} from '../../../../shared/services/authService';
import ColourTextLogo from '../../../../colour_text_logo.svg';
import MakerspaceDarkLogo from '../../../../makerspace_dark_logo.png';
import './LoginScreen.css';

const ResetPasswordScreen = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Firebase reset links carry an oobCode; `token` kept as a legacy fallback.
  const [token] = useState(searchParams.get('oobCode') || searchParams.get('token') || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }
    // Validate the code up front so an expired link fails fast.
    verifyResetCode(token).catch((err) => {
      setError(authErrorMessage(err, 'This password reset link is invalid or has expired.'));
    });
  }, [token]);

  // Password complexity validation
  const validatePasswordComplexity = (pwd) => {
    if (pwd.length < 8) {
      return { valid: false, error: 'Password must be at least 8 characters long' };
    }
    if (!/[A-Z]/.test(pwd)) {
      return { valid: false, error: 'Password must contain at least one uppercase letter' };
    }
    if (!/[a-z]/.test(pwd)) {
      return { valid: false, error: 'Password must contain at least one lowercase letter' };
    }
    if (!/[0-9]/.test(pwd)) {
      return { valid: false, error: 'Password must contain at least one number' };
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd)) {
      return { valid: false, error: 'Password must contain at least one special character' };
    }
    return { valid: true };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!token) {
      setError('Invalid reset link');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password complexity
    const passwordValidation = validatePasswordComplexity(password);
    if (!passwordValidation.valid) {
      setError(passwordValidation.error);
      setLoading(false);
      return;
    }

    try {
      await completePasswordReset(token, password);
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      console.error('Reset password error:', err);
      setError(authErrorMessage(err, 'An error occurred. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="login-screen">
        <div className="login-container">
          <div className="login-gradient-header"></div>
          <div className="login-card-content">
            <div className="login-logos">
              <img src={ColourTextLogo} alt="Framework" className="login-logo" />
              <span className="login-logo-divider">|</span>
              <img src={MakerspaceDarkLogo} alt="MakerSpace" className="login-logo login-logo-makerspace" />
            </div>
            <h1 className="login-title">Invalid Reset Link</h1>
            <p className="login-subtitle">
              This password reset link is invalid or has expired.
            </p>
            <div className="login-footer">
              <button
                type="button"
                className="toggle-mode-button"
                onClick={() => navigate('/forgot-password')}
              >
                Request New Reset Link
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-screen">
      <div className="login-container">
        <div className="login-gradient-header"></div>
        <div className="login-card-content">
          <div className="login-logos">
            <img src={ColourTextLogo} alt="Framework" className="login-logo" />
            <span className="login-logo-divider">|</span>
            <img src={MakerspaceDarkLogo} alt="MakerSpace" className="login-logo login-logo-makerspace" />
          </div>
          <h1 className="login-title">Reset Your Password</h1>
          <p className="login-subtitle">
            {success
              ? 'Password reset successfully!'
              : 'Enter your new password'}
          </p>

          {error && (
            <div className="login-error" role="alert">
              {error}
            </div>
          )}

          {success && (
            <div className="login-success" role="alert">
              Password reset successfully! Redirecting to login...
            </div>
          )}

          {!success && (
            <form onSubmit={handleSubmit} className="login-form">
              <div className="form-group">
                <label htmlFor="password">New Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters with uppercase, lowercase, number, and special character"
                  required
                  autoComplete="new-password"
                  minLength={8}
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  required
                  autoComplete="new-password"
                  minLength={8}
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                className="login-button"
                disabled={loading || !password || !confirmPassword}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          )}

          <div className="login-footer">
            <button
              type="button"
              className="toggle-mode-button"
              onClick={() => navigate('/login')}
              disabled={loading}
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordScreen;

